// src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import authConfig from '../config/auth.config';

// Create auth context
export const AuthContext = createContext({
  isAuthenticated: false,
  user: null,
  loading: true,
  verifySession: () => {},
  login: () => {},
  logout: () => {},
  hasPermission: () => {},
});

// Create a hook to use the auth context
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    // Initialize authentication from localStorage if a user exists
    const storedUser = localStorage.getItem('user');
    const initialUser = storedUser ? JSON.parse(storedUser) : null;
    
    const [isAuthenticated, setIsAuthenticated] = useState(!!initialUser);
    const [user, setUser] = useState(initialUser);
    const [loading, setLoading] = useState(!initialUser);
    const [validatedSessions, setValidatedSessions] = useState({});
  
    // Add session expiration check
    useEffect(() => {
      if (isAuthenticated) {
        // Check for login timestamp
        const loginTime = localStorage.getItem('loginTimestamp');
        
        // If no login timestamp exists, set the current time
        if (!loginTime) {
          localStorage.setItem('loginTimestamp', Date.now().toString());
        } else {
          // Check if the session has expired (6 hours = authConfig.SESSION_EXPIRY milliseconds)
          const timestamp = parseInt(loginTime);
          const currentTime = Date.now();
          
          // If the session has expired, log the user out
          if (currentTime - timestamp > authConfig.SESSION_EXPIRY) {
            logout();
          }
        }
        
        // Set up periodic check for session expiration (check every minute)
        const intervalId = setInterval(() => {
          const storedTimestamp = localStorage.getItem('loginTimestamp');
          if (storedTimestamp) {
            const loginTime = parseInt(storedTimestamp);
            const now = Date.now();
            
            // If session expired, log out
            if (now - loginTime > authConfig.SESSION_EXPIRY) {
              logout();
            }
          }
        }, 60000); // Check every minute
        
        // Clean up interval on unmount
        return () => clearInterval(intervalId);
      }
    }, [isAuthenticated]);
    
    // Function to check if user has a specific permission
    const hasPermission = (permission) => {
      if (!user || !user.permissions) {
        return false;
      }
      
      return user.permissions.includes(permission);
    };
    
    // Login function to authenticate user
    const login = async (username, password) => {
      setLoading(true);

      try {
        const { data } = await axios.post(`${authConfig.API_BASE_URL}/api/auth/login`, {
          username,
          password
        });
        
        if (data.access_token) {
          // Store login timestamp for session expiration
          localStorage.setItem('loginTimestamp', Date.now().toString());
          
          localStorage.setItem('token', JSON.stringify(data.access_token));
          
          const userData = {
            username: data.username,
            role: data.role,
            permissions: data.permissions
          };
          
          // Store user data in localStorage for persistence
          localStorage.setItem('user', JSON.stringify(userData));
          
          setUser(userData);
          setIsAuthenticated(true);
          setLoading(false);
          return true;
        } else {
          setIsAuthenticated(false);
          setUser(null);
          setLoading(false);
          return false;
        }
      } catch (error) {
        // If in development mode, allow mock login
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_AUTH === 'true') {
          // Store login timestamp for session expiration
          localStorage.setItem('loginTimestamp', Date.now().toString());
          
          const mockUser = {
            username: username,
            role: username === 'admin' ? 'admin' : 'user',
            permissions: username === 'admin' ? ['MANAGE_USERS', 'VIEW_HOSTS', 'VIEW_CLIENTS'] : ['VIEW_DASHBOARD']
          };
          
          localStorage.setItem('user', JSON.stringify(mockUser));
          setIsAuthenticated(true);
          setUser(mockUser);
          setLoading(false);
          return true;
        }
        
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return false;
      }
    };

    // Verify session function with caching
    const verifySession = async (sessionId) => {
      if (!sessionId) {
        return false;
      }

      // Check session expiration based on timestamp
      const loginTime = localStorage.getItem('loginTimestamp');
      if (loginTime) {
        const timestamp = parseInt(loginTime);
        const currentTime = Date.now();
        
        // If the session has expired (6 hours), logout and return false
        if (currentTime - timestamp > authConfig.SESSION_EXPIRY) {
          logout();
          return false;
        }
      } else {
        // If no timestamp found, set one now
        localStorage.setItem('loginTimestamp', Date.now().toString());
      }

      // Use cached session validation if available
      if (validatedSessions[sessionId] === true) {
        setIsAuthenticated(true);
        setLoading(false);
        return true;
      }
      
      setLoading(true);

      try {
        // Ensure token is set
        let token = localStorage.getItem('token');
        token = token ? JSON.parse(token) : authConfig.DEFAULT_AUTH_TOKEN;

        // Make API request with token
        const apiUrl = `${authConfig.API_BASE_URL}${authConfig.AUTH_ENDPOINT}?session_id=${sessionId}`;
        const { data } = await axios.get(apiUrl, {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          }
        });
        
        if (data.status === true) {
          // Extract user data from response
          let userDetails;
          try {
            let responseData = typeof data.response_data === 'string'
              ? JSON.parse(data.response_data)
              : data.response_data;
            
            // Extract user info based on data structure
            if (responseData?.data?.user_data?.id) {
              // Format 1: Local user
              userDetails = responseData.data.user_data;
            } else if (responseData?.data?.user_data?.idp_data) {
              // Format 2: IDP user
              const idpData = responseData.data.user_data.idp_data;
              const sessionResponse = responseData.data.user_data.session_response;
              
              // Create a normalized structure
              userDetails = {
                id: idpData.obj_id || sessionResponse.id,
                username: sessionResponse.username || idpData.name.split('@')[0],
                email: idpData.email || sessionResponse.email,
                roles: sessionResponse.roles,
                tenant: sessionResponse.tenant || responseData.data.user_data.tenant,
                is_external: sessionResponse.is_external === true
              };
            } else {
              // Unknown format - try to find any username
              userDetails = {
                username: responseData?.data?.user_data?.username || 
                         responseData?.data?.username ||
                         (responseData?.data?.user_data?.session_response?.email || '').split('@')[0] ||
                         "Unknown User"
              };
            }
          } catch (e) {
            userDetails = { username: "Unknown User" };
          }
          
          localStorage.setItem('session_id', sessionId);
          // Reset login timestamp on successful verification to maintain the 6-hour window from most recent activity
          localStorage.setItem('loginTimestamp', Date.now().toString());
          setIsAuthenticated(true);
          setUser(userDetails);
          setValidatedSessions(prev => ({ ...prev, [sessionId]: true }));
          setLoading(false);
          return true;
        } else {
          localStorage.removeItem('session_id');
          setIsAuthenticated(false);
          setUser(null);
          setValidatedSessions(prev => ({ ...prev, [sessionId]: false }));
          setLoading(false);
          return false;
        }
      } catch (error) {
        // Development fallback
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_AUTH === 'true') {
          const mockUser = {
            username: "Developer",
            roles: { role_description: "Admin" }
          };
          
          localStorage.setItem('session_id', sessionId || 'dev-session');
          setIsAuthenticated(true);
          setUser(mockUser);
          setLoading(false);
          return true;
        }
        
        localStorage.removeItem('session_id');
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        
        // Redirect to login page after a short delay
        setTimeout(() => {
          window.location.href = authConfig.REDIRECT_URL;
        }, 1000);
        
        return false;
      }
    };

    // Logout function
    const logout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('loginTimestamp');
      
      setIsAuthenticated(false);
      setUser(null);
      
      window.location.href = authConfig.REDIRECT_URL;
    };

    // Return the auth context provider
    return (
      <AuthContext.Provider
        value={{
          isAuthenticated,
          user,
          loading,
          verifySession,
          login,
          logout,
          hasPermission,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
};